import type { SVGProps } from 'react';
import { cn } from '@/lib/utils';

type Variant = 'light' | 'dark' | 'mark';
type Size = 'sm' | 'md' | 'lg';

interface BrandLogoProps {
  variant?: Variant;
  size?: Size;
  withWordmark?: boolean;
  className?: string;
  /** Accessible label; pass empty string when the logo is decorative. */
  label?: string;
}

const sizeMap: Record<Size, { mark: number; text: string }> = {
  sm: { mark: 28, text: 'text-[1.05rem]' },
  md: { mark: 36, text: 'text-[1.25rem]' },
  lg: { mark: 48, text: 'text-[1.65rem]' },
};

/**
 * TERRAMEZ brand mark.
 *
 * TERRAMEZ = TERR (fr. terre, earth) + AMEZ (amazigh: take/seize).
 *
 * Mark: Circle — minimal house silhouette with keyhole door.
 *   • House = real estate / property
 *   • Keyhole = access, ownership, the key to your home
 *   • Gold roof + cream walls + dark door + gold keyhole
 *
 *     ╭──────────╮
 *     │   /\     │  ← gold roof
 *     │  /──\    │  ← cream walls
 *     │  |⊙ |    │  ← door + gold keyhole
 *     ╰──────────╯
 */
export function BrandLogo({
  variant = 'dark',
  size = 'md',
  withWordmark = true,
  className,
  label = 'TERRAMEZ',
}: BrandLogoProps) {
  const { mark, text } = sizeMap[size];
  const wordmarkColor =
    variant === 'light' ? 'text-surface-light' : 'text-primary';

  return (
    <span
      className={cn('inline-flex items-center gap-2.5 select-none', className)}
      role="img"
      aria-label={label || undefined}
      aria-hidden={label === '' || undefined}
    >
      <BrandMark size={mark} variant={variant} />
      {withWordmark && variant !== 'mark' && (
        <span
          className={cn(
            'font-display tracking-tightish leading-none',
            text,
            wordmarkColor,
          )}
        >
          TERR
          <span className="text-accent">AMEZ</span>
        </span>
      )}
    </span>
  );
}

interface BrandMarkProps extends SVGProps<SVGSVGElement> {
  size?: number;
  variant?: Variant;
}

export function BrandMark({ size = 32, variant = 'dark', ...rest }: BrandMarkProps) {
  // Green-acres palette: forest-green primary + slate accent + cream walls.
  const bg = variant === 'light' ? '#87a44d' : '#415826';
  const accent = '#6b8e3d';
  const cream = '#f7f7f5';
  const shadow = variant === 'light' ? '#547030' : '#2e3f1c';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      {/* Circle background */}
      <circle cx="20" cy="20" r="19" fill={bg} />
      {/* Roof */}
      <polygon points="20,4 4,18 36,18" fill={accent} />
      {/* Roof eave shadow */}
      <rect x="4" y="16.5" width="32" height="2" fill={shadow} />
      {/* Walls */}
      <rect x="6" y="17" width="28" height="16" fill={cream} />
      {/* Door */}
      <rect x="14" y="21.5" width="12" height="11.5" rx="1" fill={bg} />
      {/* Keyhole — circle */}
      <circle cx="20" cy="25.5" r="2" fill={accent} />
      {/* Keyhole — slot */}
      <path d="M19.1,25.5 L18.3,30.5 L21.7,30.5 L20.9,25.5 Z" fill={accent} />
    </svg>
  );
}
